import { LoaderPage } from '@/components/pages/loading/Loading.page.component'
import { useDealSyncAuthHandler } from '@/shared/hooks/dealSync'
import { useGoogleConnectedStatus } from 'containers/deal-sync/hooks/connect-gmail'
import { UserContext } from 'context/user/UserContext'
import { AuthMiddlewareActionTypes, AuthMiddlewareState } from 'interfaces'
import { useRouter } from 'next/router'
import React from 'react'
import { AuthMiddlewareContext } from './AuthMiddlewareContext'
import { authMiddlewareReducer } from './authMiddlewareReducer'

interface Props {
  children: React.ReactNode
}

const AuthMiddleware_INITIAL_STATE: AuthMiddlewareState = {
  loading: true,
  isAuthenticated: false,
  wasValidated: false,
}

const UNAUTHENTICATED_ROUTES = [
  '/signup',
  '/login',
  '/forgot-password',
  '/reset-password',
]
const UNAUTHENTICATED_AND_UNCOMPLETE_ROUTES = [
  '/signup',
  '/login',
  '/forgot-password',
  '/reset-password',
]
const FREE_ROUTES = ['/']

export const AuthMiddlewareProvider: React.FC<Props> = ({ children }) => {
  const [state, dispatch] = React.useReducer(
    authMiddlewareReducer,
    AuthMiddleware_INITIAL_STATE
  )
  const { user, loading } = React.useContext(UserContext)
  const [redirecting, setRedirecting] = React.useState('')
  const [isFirstTime] = React.useState(true)
  const { isDealSyncLogin } = useDealSyncAuthHandler()
  const { data: googleConnectedStatus, isLoading: loadingGoogle } =
    useGoogleConnectedStatus()
  const isGoogleConnected = googleConnectedStatus?.gmailConnected
  const router = useRouter()
  React.useEffect(() => {
    if (router) {
      const checkNewUrls = (e: string) => {
        const nextRoute = e

        if (!e || redirecting) return

        if (UNAUTHENTICATED_ROUTES.includes(nextRoute)) {
          if (
            UNAUTHENTICATED_AND_UNCOMPLETE_ROUTES.includes(nextRoute) &&
            !state.isComplete &&
            !redirecting
          ) {
            dispatch({
              type: AuthMiddlewareActionTypes.SET_LOADING,
              payload: false,
            })
            return
          }

          dispatch({
            type: AuthMiddlewareActionTypes.SET_LOADING,
            payload: true,
          })
          setRedirecting('/' + user?.username)
          router.push('/' + user?.username)
          return
        }

        if (FREE_ROUTES.includes(nextRoute)) {
          dispatch({
            type: AuthMiddlewareActionTypes.SET_LOADING,
            payload: false,
          })
          return
        }

        if (
          !state.isAuthenticated ||
          !state.isComplete ||
          (isDealSyncLogin() && !isGoogleConnected && !loadingGoogle)
        ) {
          dispatch({
            type: AuthMiddlewareActionTypes.SET_LOADING,
            payload: true,
          })
          setRedirecting('/signup')
          router.push('/signup')
          return
        }

        dispatch({
          type: AuthMiddlewareActionTypes.SET_LOADING,
          payload: false,
        })
      }

      // TODO: this will be deactive until we have the new pre-profile page
      // router.events.on('routeChangeComplete', afterChange)
      // router.events.on('routeChangeStart', checkNewUrls)

      // if (isFirstTime && !loading) {
      //   checkNewUrls(router.asPath)
      //   setIsFirstTime(false)
      // }

      return () => {
        router.events.off('routeChangeStart', checkNewUrls)
      }
    }
  }, [
    redirecting,
    router,
    state.isAuthenticated,
    state.isComplete,
    isFirstTime,
    loadingGoogle,
    user?.username,
    loading,
    isDealSyncLogin,
    isGoogleConnected,
  ])

  React.useEffect(() => {
    dispatch({
      type: AuthMiddlewareActionTypes.SET_AUTHENTICATED,
      payload: user?.id ? true : false,
    })
    dispatch({
      type: AuthMiddlewareActionTypes.SET_IS_COMPLETE,
      payload: user?.is_complete ? true : false,
    })
  }, [user, loading])

  return (
    <AuthMiddlewareContext.Provider
      value={{
        ...state,
      }}
    >
      {loading ? <LoaderPage /> : children}
    </AuthMiddlewareContext.Provider>
  )
}
